<template>
  <div class="">
    <!-- Header and Add Button -->
    <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
      <div>
        <h2 class="text-xl font-semibold text-gray-800">List of Music categories</h2>
      </div>
      <button
        @click="addingNewItem"
        class="flex items-center gap-2 bg-blue-800 hover:bg-blue-900 text-white font-semibold px-3 py-2 rounded-lg shadow transition"
      >
        <span class="material-icons text-base">add</span>
        Add Category
      </button>
    </div>
    <!-- Filter Bar -->
    <div class="flex flex-wrap items-center justify-end gap-2 mb-4">
      <!--  Search and Sort Controls -->
      <div class="flex items-center gap-2">
        <vs-select v-model="search.language" @change="searchData" class="w-28" :color="'#6366f1'">
          <vs-select-item value="all" text="Language" disabled />
          <vs-select-item v-for="lang in languages" :key="lang.culture" :value="lang.culture" :text="lang.culture" />
        </vs-select>
        <vs-select v-model="search.sort" @change="searchData" class="w-28" :color="'#6366f1'">
          <vs-select-item value="ASC" text="Ascending" />
          <vs-select-item value="DESC" text="Descending" />
        </vs-select>
        <button
          @click="sortPositions()"
          class="flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition"
        >
          <span class="material-icons text-base">sort</span> Sort
        </button>
        <button
          @click="resetData()"
          class="flex items-center gap-1 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg font-medium transition"
        >
          <span class="material-icons text-base">refresh</span> Reset
        </button>
      </div>
    </div>
    <!-- Music Categories Table -->
    <div class="overflow-x-auto rounded-xl border border-gray-200 bg-white pb-2">
      <table class="min-w-full bg-white rounded-xl">
        <thead class="border-b">
          <tr>
            <th class="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">#</th>
            <th class="px-4 py-3 text-xs text-start font-semibold text-gray-500 uppercase">Feature Image</th>
            <th class="px-4 py-3 text-xs text-start font-semibold text-gray-500 uppercase">Image</th>
            <th class="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Name</th>
            <th class="px-4 py-3 text-xs text-center font-semibold text-gray-500 uppercase cursor-pointer" @click="sortTable('position')">
              Position
              <span v-if="sortColumn === 'position'">
                {{ sortOrder === "asc" ? "▲" : "▼" }}
              </span>
            </th>
            <th class="px-4 py-3 text-xs text-center font-semibold text-gray-500 uppercase">Language</th>
            <th class="px-4 py-3 text-xs text-center font-semibold text-gray-500 uppercase cursor-pointer" @click="sortTable('totalPlays')">
              Total Plays
              <span v-if="sortColumn === 'totalPlays'">
                {{ sortOrder === "asc" ? "▲" : "▼" }}
              </span>
            </th>
            <th class="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(cat, idx) in all"
            :key="cat._id"
            class="border-b hover:bg-gray-100 transition"
            :title="`Click to view music under ${cat.name} category`"
            @click="goToMusicList(cat._id)"
          >
            <td class="px-4 py-1">{{ idx + 1 + (currentPage - 1) * limit }}</td>
            <td class="">
              <img
                :src="cat.featureImage || require('@/assets/images/No-image.png')"
                class="rounded-md object-cover"
                style="width: 90px; height: 60px"
              />
            </td>
            <td class="">
              <img :src="cat.image || require('@/assets/images/No-image.png')" class="rounded-md object-cover" style="width: 90px; height: 60px" />
            </td>
            <td class="px-4 py-1 font-medium text-gray-800">{{ cat.name }}</td>
            <td class="px-4 py-1 text-center">
              <div class="flex flex-col items-center gap-2">
                <button
                  class="flex items-center justify-center border-2 border-gray-100 rounded-md w-6 h-6 disabled:opacity-50"
                  :disabled="cat.position === 1"
                  @click.stop="updatePosition(cat._id, search.sort === 'ASC' ? 'up' : 'down')"
                >
                  <svg
                    :class="cat.position === 1 ? 'text-gray-200' : 'text-gray-400'"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    class="w-10 h-10"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 15l-7-7-7 7" />
                  </svg>
                </button>
                <span class="font-semibold text-gray-700">{{ cat.position }}</span>
                <button
                  class="flex items-center justify-center border-2 border-gray-100 rounded-md w-6 h-6 disabled:opacity-50"
                  :disabled="cat.position === all.length"
                  @click.stop="updatePosition(cat._id, search.sort === 'ASC' ? 'down' : 'up')"
                >
                  <svg
                    :class="cat.position === all.length ? 'text-gray-200' : 'text-gray-400'"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    class="w-10 h-10"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            </td>
            <td
              class="px-4 py-1 text-center"
              :title="cat.missingLanguages.length > 0 ? `Missing: ${cat.missingLanguages.join(', ')}` : 'All languages available'"
            >
              <div class="flex items-center justify-center gap-2">
                <span class="text-gray-500 text-lg">{{ cat.language }}</span>
                <span
                  v-if="cat.languageCount && cat.missingLanguages && cat.language === 'EN'"
                  :class="[
                    'inline-flex items-center px-2 py-1 rounded-full font-semibold cursor-pointer',
                    cat.languageCount - cat.missingLanguages.length !== 11 ? 'bg-red-900 text-white' : 'bg-blue-900 text-white',
                  ]"
                >
                  {{ cat.languageCount - cat.missingLanguages.length }}/{{ cat.languageCount }}
                </span>
                <span v-if="cat.language === 'EN'" class="material-icons text-gray-400 cursor-pointer" style="font-size: 20px">info</span>
              </div>
            </td>
            <td class="px-4 py-1 text-center font-semibold text-gray-700">
              <!-- show recentPlays and totaLPlays (recent plays is 24h ) -->
              <div class="flex flex-col items-center">
                <span class="text-sm text-gray-500">24h plays</span>
                <span class="text-sm">{{ cat.recentPlays || 0 }}</span>
                <span class="text-sm text-gray-500">Total plays</span>
                <span class="text-sm">{{ cat.totalPlays || 0 }}</span>
              </div>
            </td>
            <td class="px-4 py-1">
              <vs-dropdown>
                <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="5" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="12" cy="19" r="1.5" />
                </svg>
                <vs-dropdown-menu>
                  <vs-dropdown-item @click="editCategory(cat)"> <vs-icon icon="create" class="align-right" /> Edit </vs-dropdown-item>
                  <vs-dropdown-item @click="deleteCategory(cat)"> <vs-icon icon="delete_sweep" /> Delete </vs-dropdown-item>
                </vs-dropdown-menu>
              </vs-dropdown>
            </td>
          </tr>
          <tr v-if="!all || all.length === 0">
            <td colspan="12" class="text-center h-full py-6 text-gray-400">No categories found.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- Pagination -->
    <div class="flex flex-col md:flex-row items-center justify-between mt-3 pr-3">
      <div class="invisible">
        <span>asd</span>
      </div>
      <div class="flex justify-center items-center">
        <vs-pagination v-model="currentPage" :total="totalPages" :limit="1" @change="searchData" class="my-2" />
      </div>
      <div class="flex items-center gap-2 text-sm mt-2 md:mt-0">
        <span>Show Records:</span>
        <vs-select v-model="limit" @change="searchData" class="w-20" :color="'#6366f1'">
          <vs-select-item value="25" text="25" />
          <vs-select-item value="50" text="50" />
          <vs-select-item value="100" text="100" />
        </vs-select>
      </div>
    </div>

    <!-- Add Category Popup -->
    <vs-popup :title="isEdit ? 'Edit Category' : 'Add New Category'" :active.sync="addCategoryPopup">
      <form @submit.prevent="isEdit ? handleUpdateCategory() : handleSaveCategory()">
        <!-- language filter -->
        <div class="mb-2 px-2 flex justify-between">
          <div class="w-1/4" v-if="isEdit">
            <label class="block text-sm font-semibold mb-2">Language</label>
            <vs-select
              v-model="newCat.language"
              @change="refetchUsingLangugage($event, newCat)"
              class="w-full"
              :color="'#6366f1'"
              :disabled="showLoader"
            >
              <vs-select-item value="" text="Select Language" disabled />
              <vs-select-item v-for="lang in languages" :key="lang.culture" :value="lang.culture" :text="lang.culture" />
            </vs-select>
          </div>
          <div v-if="isEdit && newCat.language === 'EN'" :title="'Toggle to update existing translations'">
            <!-- toggle pill to update existing translations -->
            <label class="block text-sm font-semibold mb-2">Update Translations</label>
            <toggle-pill v-model="newCat.translate" :disabled="showLoader" />
          </div>
        </div>
        <div class="px-2">
          <label class="block text-sm font-semibold mb-2">Name</label>
          <vs-input v-model="newCat.name" placeholder="Keep short and simple" class="w-full" required :disabled="showLoader" />
          <div class="mb-6 flex flex-col md:flex-row md:items-center md:gap-6">
            <div class="flex-1" style="width: 100px; height: 150px">
              <div class="flex justify-between items-start">
                <!-- image -->
                <label class="block text-sm font-semibold">Image</label>
                <button
                  v-if="displayImage"
                  type="button"
                  color="danger"
                  class="bg-red-500 p-1 h-6 rounded-md hover:bg-red-700 transition text-white"
                  @click="clearImage"
                  :disabled="showLoader"
                >
                  Clear
                </button>
              </div>
              <!-- image -->
              <div
                class="flex flex-col items-center justify-center border-2 border-dashed rounded-md cursor-pointer bg-gray-50 hover:bg-gray-100 transition"
                style="width: 100%; height: 140px; min-height: 140px"
                @dragover.prevent
                @dragenter.prevent
                @drop.prevent="onDropImage"
                @click="triggerFileInput"
              >
                <img v-if="displayImage" :src="displayImage" class="rounded-md object-cover border" style="width: 200px; max-height: 140px" />
                <div v-else class="flex flex-col items-center justify-center w-full h-full text-gray-400">
                  <span class="material-icons" style="font-size: 32px">cloud_upload</span>
                  <span>Drag & Drop or Click to Upload</span>
                </div>
                <input
                  type="file"
                  ref="fileInput"
                  class="hidden"
                  @change="onImageChange"
                  accept="image/jpeg,image/jpg,image/webp"
                  :disabled="showLoader"
                />
              </div>
            </div>
            <!-- feature image -->
            <div class="flex-1" style="width: 100px; height: 150px">
              <div class="flex justify-between items-center">
                <label class="block text-sm font-semibold">Feature Image</label>
                <button
                  v-if="newCat.featureImage"
                  type="button"
                  color="danger"
                  class="bg-red-500 p-1 h-6 rounded-md hover:bg-red-700 transition text-white"
                  @click="clearFeatureImage"
                  :disabled="showLoader"
                >
                  Clear
                </button>
              </div>
              <div
                class="flex flex-col items-center justify-center border-2 border-dashed rounded-md cursor-pointer bg-gray-50 hover:bg-gray-100 transition"
                style="width: 100%; height: 140px; min-height: 140px"
                @dragover.prevent
                @dragenter.prevent
                @drop.prevent="onDropImageFeature"
                @click="triggerFeatureFileInput"
              >
                <img
                  v-if="displayImageFeature"
                  :src="displayImageFeature"
                  class="rounded-md object-cover border"
                  style="width: 200px; max-height: 140px"
                />
                <div v-else class="flex flex-col items-center justify-center w-full h-full text-gray-400">
                  <span class="material-icons" style="font-size: 32px">cloud_upload</span>
                  <span>Drag & Drop or Click to Upload</span>
                </div>
                <input
                  type="file"
                  ref="featureFileInput"
                  class="hidden"
                  @change="onFeatureImageChange"
                  accept="image/jpeg,image/jpg,image/webp"
                  :disabled="showLoader"
                />
              </div>
            </div>
          </div>
          <div class="flex-1 pt-2">
            <label class="block text-sm font-semibold mb-2"
              >Position
              <span v-if="isEdit" class="font-bold">: {{ newCat.position ? newCat.position : "Not set" }}</span>
            </label>
            <div class="flex gap-4 mb-3">
              <vs-checkbox v-model="newCat.moveTop" @change="onMoveTopChange" :disabled="showLoader">Move to Top</vs-checkbox>
              <vs-checkbox v-model="newCat.moveBottom" @change="onMoveBottomChange" :disabled="showLoader">Move to Bottom</vs-checkbox>
            </div>
          </div>
          <!-- independent language to translate -->
          <div class="pt-2" v-if="!isEdit">
            <label class="block text-sm font-semibold mb-2">Choose Languages to Translate</label>
            <div class="grid grid-cols-7 gap-2">
              <vs-checkbox v-model="selectAllLang" @change="toggleSelectAll" :disabled="showLoader" class="z-0">All</vs-checkbox>
              <div v-for="lang in languages" :key="lang.culture">
                <vs-checkbox
                  :value="newCat.languagesToTranslate.includes(lang.culture)"
                  @change="toggleLanguage(lang.culture)"
                  :disabled="lang.culture === 'EN' || showLoader"
                  class="z-0"
                >
                  {{ lang.culture }}
                </vs-checkbox>
              </div>
            </div>
          </div>
          <!-- add missing languages -->
          <div v-if="isEdit && newCat.language === 'EN' && newCat.missingLanguages && newCat.missingLanguages.length > 0">
            <label class="block text-sm font-semibold pt-1 mb-2">Add missing translations</label>
            <vs-checkbox v-model="newCat.translateMissing" :disabled="showLoader">Add missing translations</vs-checkbox>
            <div v-if="newCat.translateMissing" class="grid grid-cols-7 gap-2 mt-3">
              <vs-checkbox v-model="selectAllEdit" @change="toggleSelectAllEdit" :disabled="showLoader" class="z-0">All</vs-checkbox>
              <div v-for="lang in newCat.missingLanguages" :key="lang">
                <vs-checkbox
                  :value="newCat.languagesToTranslate.includes(lang) && lang !== 'EN'"
                  :disabled="lang === 'EN' || showLoader"
                  @change="toggleLanguage(lang)"
                  class="z-0"
                >
                  {{ lang }}
                </vs-checkbox>
              </div>
            </div>
          </div>

          <div v-if="showLoader" class="mb-4 text-blue-700">Translations are working in background...</div>

          <div class="flex flex-col md:flex-row gap-3 mt-8">
            <vs-button
              v-if="isEdit && !newCat.translateMissing"
              :loading="showLoader"
              color="primary"
              type="filled"
              class="flex-1"
              @click="handleUpdateCategory"
              :disabled="showLoader"
            >
              Update current
            </vs-button>
            <vs-button
              v-if="isEdit && newCat.language === 'EN'"
              :loading="showLoader"
              color="primary"
              type="filled"
              class="flex-1"
              @click="handleUpdateAllCategory"
              :disabled="showLoader"
            >
              {{ newCat.translateMissing ? "Update And Translate Missing" : "Update All" }}
            </vs-button>
            <vs-button
              v-if="!isEdit"
              :loading="showLoader"
              color="primary"
              type="filled"
              class="flex-1"
              @click="handleSaveCategory"
              :disabled="showLoader"
            >
              {{ newCat.languagesToTranslate.length > 1 ? "Save and Translate" : "Save English" }}
            </vs-button>
            <!-- save and translate toggle
            <div class="flex flex-col" v-if="!isEdit" style="width: 50px">
              <label class="block text-xs font-semibold">Translate?</label>
              <toggle-pill v-model="newCat.translate" :disabled="showLoader" />
            </div> -->
          </div>
        </div>
      </form>
    </vs-popup>

    <!-- Delete Confirmation Popup -->
    <vs-popup title="Delete Category" :active.sync="deleteCategoryPopup">
      <p class="mb-4 text-lg text-gray-700">Are you sure you want to delete this category?</p>
      <div class="flex justify-end gap-2">
        <vs-button color="warning" @click="confirmDeleteCategory">Delete</vs-button>
        <vs-button color="primary" @click="deleteCategoryPopup = false">Cancel</vs-button>
      </div>
    </vs-popup>

    <!-- select category popup -->
    <vs-popup title="Select the god category to which you want to add music to" :active.sync="showCategorySelection">
      <div class="flex flex-col gap-1 max-h-96 overflow-y-auto mb-3">
        <button
          v-for="cat in all"
          :key="cat._id"
          class="w-full text-left text-base px-2 py-2 bg-gray-100 hover:bg-blue-200 rounded-lg transition"
          @click="goToMusicList(cat._id, true)"
        >
          {{ cat.name }}
        </button>
      </div>
      <div class="flex justify-end">
        <vs-button color="primary" @click="showCategorySelection = false">Cancel</vs-button>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import api from "@/rest.js";
import { API } from "@/common/constants";
import TogglePill from "../components/Common/TogglePill.vue";

export default {
  name: "MusicCategory",
  components: {
    TogglePill,
  },
  data() {
    return {
      search: {
        sort: "ASC",
        language: "EN",
        status: "0",
      },
      languages: [],
      all: [],
      currentPage: 1,
      totalPages: 1,
      limit: 25,
      deleteCategoryPopup: false,
      itemToDelete: null,
      addCategoryPopup: false,
      selectAllLang: false,
      isEdit: false,
      newCat: {
        missingLanguages: [],
        status: true,
        language: "",
        position: "",
        name: "",
        image: "",
        featureImage: "",
        moveTop: false,
        moveBottom: false,
        translate: false,
        translateMissing: false,
        languagesToTranslate: [],
      },
      displayImage: null,
      displayImageFeature: null,
      showLoader: false,
      showCategorySelection: false,
      sortColumn: null,
      sortOrder: "asc",
    };
  },
  methods: {
    toggleSelectAll() {
      if (this.selectAllLang) {
        this.newCat.languagesToTranslate = this.languages.map((lang) => lang.culture);
      } else {
        this.newCat.languagesToTranslate = ["EN"];
      }
    },

    toggleSelectAllEdit() {
      if (this.selectAllEdit) {
        this.newCat.languagesToTranslate = this.newCat.missingLanguages;
      } else {
        this.newCat.languagesToTranslate = ["EN"];
      }
    },

    toggleLanguage(culture) {
      const selectedLanguages = this.newCat.languagesToTranslate;

      if (selectedLanguages.includes(culture)) {
        this.newCat.languagesToTranslate = selectedLanguages.filter((lang) => lang !== culture);
      } else {
        this.newCat.languagesToTranslate = [...selectedLanguages, culture];
      }

      const allLanguagesSelected = this.languages.every((lang) => this.newCat.languagesToTranslate.includes(lang.culture));
      this.selectAllLang = allLanguagesSelected;
      if (this.selectAllEdit) {
        const allLanguagesSelected = this.newCat.missingLanguages.every((lang) => this.newCat.languagesToTranslate.includes(lang));
        this.selectAllEdit = allLanguagesSelected;
      }
    },

    goToMusicList(catId, fromSelection = false) {
      this.$router.push({
        path: `/musiclistDisplay/${catId}`,
        query: {
          catLang: this.$data.search.language,
          fromSelection: fromSelection,
        },
      });
      this.showCategorySelection = false;
    },

    resetData() {
      this.$data.search = {
        sort: "ASC",
        language: "EN",
      };
      this.$data.limit = 25;
      this.searchData();
    },

    onMoveTopChange() {
      if (this.newCat.moveTop) {
        this.newCat.moveBottom = false;
      } else if (!this.newCat.moveBottom) {
        this.newCat.moveBottom = true;
      }
    },

    onMoveBottomChange() {
      if (this.newCat.moveBottom) {
        this.newCat.moveTop = false;
      } else if (!this.newCat.moveTop) {
        this.newCat.moveBottom = true;
      }
    },

    editCategory(cat) {
      this.isEdit = true;
      this.addCategoryPopup = true;
      this.newCat = {
        ...cat,
        translate: false,
        translateMissing: false,
        languagesToTranslate: ["EN"],
      };
      this.displayImage = cat.image;
      this.displayImageFeature = cat.featureImage;
      this.selectAllLang = false;
    },

    deleteCategory(cat) {
      this.itemToDelete = cat;
      this.deleteCategoryPopup = true;
    },

    addingNewItem() {
      this.clearFeatureImage();
      this.clearImage();
      this.isEdit = false;
      this.newCat = {
        missingLanguages: [],
        status: true,
        featureImage: "",
        image: "",
        position: "",
        name: "",
        language: "EN",
        moveBottom: true,
        moveTop: false,
        translate: false,
        translateMissing: false,
        languagesToTranslate: ["EN"],
      };
      this.selectAllLang = false;
      this.selectAllEdit = false;
      this.addCategoryPopup = true;
    },

    onImageChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.newCat.image = file;
        this.displayImage = URL.createObjectURL(file);
      }
    },

    onFeatureImageChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.newCat.featureImage = file;
        this.displayImageFeature = URL.createObjectURL(file);
      } else {
        this.displayImageFeature = ""; // Clear preview if no file is selected
      }
    },

    clearImage() {
      this.displayImage = "";
      this.newCat.image = "";
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    clearFeatureImage() {
      this.displayImageFeature = "";
      this.newCat.featureImage = "";
      if (this.$refs.featureFileInput) {
        this.$refs.featureFileInput.value = "";
      }
    },

    triggerFileInput() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },

    triggerFeatureFileInput() {
      if (this.$refs.featureFileInput) this.$refs.featureFileInput.click();
    },

    onDropImage(e) {
      const files = e.dataTransfer.files;
      if (files && files[0]) {
        this.onImageChange({ target: { files } });
      }
    },

    onDropImageFeature(e) {
      const files = e.dataTransfer.files;
      if (files && files[0]) {
        this.onFeatureImageChange({ target: { files } });
      }
    },

    async updatePosition(id, type) {
      this.$vs.loading();
      try {
        const r = await api.put(`${API.GOD_UPDATE_POSITION}/${id}?direction=${type}`, {});
        this.$vs.loading.close();
        if (r.status === 200) {
          this.$vs.notify({
            text: "Position Updated Successfully",
            color: "success",
            position: "top-right",
          });
          await this.searchData();
        }
      } catch (e) {
        this.$vs.notify({
          text: e.response.data.error || "Error updating position",
          color: "danger",
          position: "top-right",
        });
        this.$vs.loading.close();
      }
    },

    async sortPositions() {
      this.$vs.loading();
      try {
        const r = await api.put(`${API.GOD_SORT_POSITION}?language=${this.$data.search.language}`, {});
        if (r.status === 200) {
          this.$vs.notify({
            text: "Sorted Successfully",
            color: "success",
            position: "top-right",
          });
          await this.searchData();
        }
      } catch (error) {
        this.$vs.notify({
          text: "Error Sorting",
          color: "danger",
          position: "top-right",
        });
        this.$vs.loading.close();
      } finally {
        this.$vs.loading.close();
      }
    },

    async refetchUsingLangugage(event, item) {
      let id = item.ref || item._id;
      if (!event && !id) return;

      this.$vs.loading();
      try {
        const res = await api.get(`${API.GET_SINGLE_GOD}/${id}?language=${event}`);
        if (res.status === 200) {
          this.$data.newCat = Object.assign({}, res.data);
          this.$data.displayImage = res.data.image;
          this.$data.featureImage = res.data.featureImage;
          await this.searchData();
        }
      } catch (error) {
        this.$vs.notify({
          text: "Error fetching category",
          color: "danger",
          position: "top-right",
        });
        console.error("Error fetching category:", error);
      } finally {
        this.$vs.loading.close();
      }
    },

    async confirmDeleteCategory() {
      this.$vs.loading();

      const r = await api.delete(API.DELETE_GOD, { params: { id: this.itemToDelete._id } });

      try {
        if (r.status == 200) {
          this.$vs.notify({
            text: "Deleted Successfully",
            color: "success",
            position: "top-right",
          });
        }
        this.$data.deleteCategoryPopup = false;
        await this.searchData();
      } catch (error) {
        this.$vs.notify({
          text: "Error Deleting",
          color: "danger",
          position: "top-right",
        });
        this.$vs.loading.close();
      }
    },

    async handleUploadImage(file) {
      if (!file || !(file instanceof File)) {
        return null;
      }

      let imageUrl = "";
      let formData = new FormData();
      formData.append("file", file);

      try {
        const response = await api.post(API.UPLOAD_IMAGE, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (response.data) {
          imageUrl = response.data.files[0].url;
        }
      } catch (e) {
        this.$vs.loading.close();
        this.$vs.notify({
          text: "File Uploading Failed",
          color: "danger",
          position: "top-right",
        });
        return console.log(e);
      } finally {
        this.$vs.loading.close();
      }
      return imageUrl;
    },

    async handleUpdateCategory() {
      if (!this.newCat.name || !this.newCat.image || !this.newCat.featureImage) {
        this.$vs.notify({
          text: "Please fill all required fields",
          color: "danger",
          position: "top-right",
        });
        return;
      }

      let imageUrl;
      let featureImageUrl;

      this.$vs.loading();

      if (this.newCat.image instanceof File) {
        imageUrl = await this.handleUploadImage(this.newCat.image);
      } else {
        imageUrl = this.newCat.image;
      }

      if (this.newCat.featureImage instanceof File) {
        featureImageUrl = await this.handleUploadImage(this.newCat.featureImage);
      } else {
        featureImageUrl = this.newCat.featureImage;
      }

      this.showLoader = this.newCat.translate;
      const data = {
        ...this.newCat,
        image: imageUrl,
        featureImage: featureImageUrl,
      };

      try {
        const res = await api.put(API.UPDATE_GOD, data, { params: { id: this.newCat._id } });

        if (res.status === 200) {
          this.$vs.notify({
            text: "Category Updated Successfully",
            color: "success",
            position: "top-right",
          });
          this.addCategoryPopup = false;
          this.isEdit = false;
          await this.searchData();
        }
      } catch (error) {
        this.$vs.notify({
          text: "Error Updating Category",
          color: "danger",
          position: "top-right",
        });
        console.error("Error updating category:", error);
      } finally {
        this.$vs.loading.close();
      }
    },

    async handleUpdateAllCategory() {
      if (!this.newCat.name || !this.newCat.image || !this.newCat.featureImage) {
        this.$vs.notify({
          text: "Please fill all required fields",
          color: "danger",
          position: "top-right",
        });
        return;
      }

      let imageUrl;
      let featureImageUrl;

      this.$vs.loading();

      if (this.newCat.image instanceof File) {
        imageUrl = await this.handleUploadImage(this.newCat.image);
      } else {
        imageUrl = this.newCat.image;
      }

      if (this.newCat.featureImage instanceof File) {
        featureImageUrl = await this.handleUploadImage(this.newCat.featureImage);
      } else {
        featureImageUrl = this.newCat.featureImage;
      }

      if (!imageUrl || !featureImageUrl) {
        this.$vs.loading.close();
        this.$vs.notify({
          text: "Image upload failed",
          color: "danger",
          position: "top-right",
        });
        return;
      }

      const data = {
        ...this.newCat,
        image: imageUrl,
        featureImage: featureImageUrl,
        languagesToTranslate: this.newCat.languagesToTranslate,
      };

      try {
        const res = await api.put(`${API.U_AND_T_GOD}/${this.newCat._id}`, data);

        if (res.status === 200) {
          this.$vs.notify({
            text: "Category Updated Successfully for all languages",
            color: "success",
            position: "top-right",
          });
          this.addCategoryPopup = false;
          this.isEdit = false;
          await this.searchData();
        }
      } catch (error) {
        this.$vs.notify({
          text: "Error Updating Category for all languages",
          color: "danger",
          position: "top-right",
        });
        console.error("Error updating category for all languages:", error);
      } finally {
        this.$vs.loading.close();
      }
    },

    async handleSaveCategory() {
      if (!this.newCat.name || !this.newCat.image || !this.newCat.featureImage) {
        this.$vs.notify({
          text: "Please fill all required fields",
          color: "danger",
          position: "top-right",
        });
        return;
      }
      this.$vs.loading();

      try {
        const [imageUrl, featureImageUrl] = await Promise.all([
          this.handleUploadImage(this.newCat.image),
          this.handleUploadImage(this.newCat.featureImage),
        ]);

        if (!imageUrl || !featureImageUrl) {
          this.$vs.loading.close();
          this.$vs.notify({
            text: "Image upload failed",
            color: "danger",
            position: "top-right",
          });
          return;
        }

        const data = {
          ...this.newCat,
          image: imageUrl,
          featureImage: featureImageUrl,
        };

        this.showLoader = this.newCat.languagesToTranslate.length > 1;

        let response;
        if (this.newCat.languagesToTranslate.length > 1) {
          data.languagesToTranslate = this.$data.newCat.languagesToTranslate;
          response = await api.post(API.C_AND_T_GOD, data);
        } else {
          response = await api.post(API.INSERT_GOD, data);
        }

        if (response.status === 200) {
          this.$vs.notify({
            text: "Category Added Successfully",
            color: "success",
            position: "top-right",
          });
          this.addCategoryPopup = false;
          this.isEdit = false;
          await this.searchData();
        }
      } catch (error) {
        this.$vs.notify({
          text: "Error Saving Category",
          color: "danger",
          position: "top-right",
        });
        console.error("Error saving category:", error);
      } finally {
        this.$vs.loading.close();
        this.showLoader = false;
      }
    },

    async searchData() {
      this.$vs.loading();
      try {
        const r = await api.get(API.GET_GODS_DASH, { params: { position: this.$data.search.sort, language: this.$data.search.language } });
        this.$vs.loading.close();

        if (this.$data.search.language === "EN") {
          for (const item of r.data) {
            item.tooltipContent =
              item.missingLanguages.length > 0 ? "Missing Languages: " + item.missingLanguages.join(", ") : "All Languages are translated";
          }
        }

        this.$data.all = r.data;
        if (r.status != 200) {
          this.$vs.notify({
            title: "Error",
            text: r.data.error,
            color: "danger",
            position: "top-right",
          });
        }
      } catch (error) {
        this.$vs.loading.close();
      }
    },

    async confirmDeleteCategory() {
      let id = this.$data.itemToDelete._id;

      const r = await api.delete(API.DELETE_GOD, { params: { id: id } });

      try {
        if (r.status == 200) {
          this.$vs.notify({
            text: "Deleted Successfully",
            color: "success",
            position: "top-right",
          });
        }
        this.$data.deleteCategoryPopup = false;
        await this.searchData();
      } catch (error) {
        this.$vs.notify({
          text: "Error Deleting",
          color: "danger",
          position: "top-right",
        });
        this.$vs.loading.close();
        console.log(error);
      }
    },

    async fetchAllLanguages() {
      this.$vs.loading();
      try {
        const r = await api.get(API.GET_LANG, {});
        this.languages = r.data;
      } catch (error) {
        this.$vs.notify({
          text: "Error fetching languages",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    sortTable(column) {
      if (this.sortColumn === column) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortColumn = column;
        this.sortOrder = "asc";
      }

      this.all.sort((a, b) => {
        let valA = a[column];
        let valB = b[column];

        if (valA == null) valA = 0;
        if (valB == null) valB = 0;

        if (this.sortOrder === "asc") {
          return valA > valB ? 1 : valA < valB ? -1 : 0;
        } else {
          return valA < valB ? 1 : valA > valB ? -1 : 0;
        }
      });
    },
  },

  async mounted() {
    await Promise.all([this.searchData(), this.fetchAllLanguages()]);
    if (this.$route.query.addNew === "true") {
      this.showCategorySelection = true;
      this.$router.replace({ query: null });
    }
  },
};
</script>

<style scoped>
.material-icons {
  font-family: "Material Icons";
  font-style: normal;
  font-weight: normal;
  font-size: 20px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  direction: ltr;
  -webkit-font-smoothing: antialiased;
}
</style>
